import { Table } from 'rsuite';
import {
  generateAccountNodeId,
  getPreSelectedNode,
  getSelectedTrees,
  ITreeData,
  selectOU,
  selectOUStatus
} from '@monorepo/shared/store/fcOuSlice';
import { useEffect, useMemo, useState } from 'react';
import CollaspedOutlineIcon from '@rsuite/icons/CollaspedOutline';
import ExpandOutlineIcon from '@rsuite/icons/ExpandOutline';
import { useGetUserOrgNodesQuery } from '@monorepo/shared/services/globalApi';
import { ExpandedRow, renderNormalRow } from './RenderRow';
import { useAppDispatch, useAppSelector } from '@monorepo/shared/store/hook';
import {
  updateLocalLatestAccount,
  regionAssetApi,
  regionInstanceApi,
  regionUserHelperApi,
  useLazyGetInstByAccountQuery,
  useLazyGetAssetByAccountQuery,
  getLocalGroupOpenAccount,
  updateLocalGroupOpenAccount,
  useLazyGetInstanceExtraInfoQuery,
  MY_ACCOUNT_LIST
} from '@monorepo/shared';
import {
  selectAccounts,
  selectCurrentAccount,
  selectIsRedirectChecked,
  updateCurrentAccount,
  updateCurrentGroupAccount
} from '@monorepo/shared/store/accountSlice';
import { selectCurrentGroup } from '@monorepo/shared/store/groupSlice';

import { OrgPagination } from './OrgPagination';
import { HomeElms, server } from '../../config/const';
import { OrgToolbar } from './OrgToolbar';
import { useOutletContext } from 'react-router-dom';
import { selectRegions } from '@monorepo/shared/store/authSlice';
import './Organization.scss';

const Organization = () => {
  const { isRedirectValid } = useOutletContext<any>();
  const rowKey = 'value';
  const regions = useAppSelector(selectRegions);
  const accounts = useAppSelector(selectAccounts);
  const ouStatus = useAppSelector(selectOUStatus);
  const ouData = useAppSelector(selectOU);
  const ouPathMap = useMemo(() => {
    const map = new Map<string | number, string[]>();
    const walk = (nodes: Record<string, any>[], path: string[]) => {
      for (const node of nodes) {
        const nextPath = path.concat(node?.label);
        if (node?.nodeId && !map.has(node.nodeId)) {
          map.set(node.nodeId, nextPath);
        }
        if (node?.children?.length) {
          walk(node.children, nextPath);
        }
      }
    };
    walk(ouData || [], []);
    return map;
  }, [ouData]);
  const preSelectedNode = useAppSelector(getPreSelectedNode);
  const currentGroup = useAppSelector(selectCurrentGroup);
  const isRedirectChecked = useAppSelector(selectIsRedirectChecked);
  const [treeData, setTreeData] = useState<Record<string, any>[]>([]);
  const [tableData, setTableData] = useState<Record<string, any>[]>([]);
  const [sortColumn, setSortColumn] = useState<string>('account_id');
  const [sortType, setSortType] = useState<any>('asc');
  const [loading, setLoading] = useState<boolean>(false);
  const [sortData, setSortData] = useState<Record<string, any>[]>([]);
  const [groupSearchKeyValue, setGroupSearchKeyValue] = useState<
    Record<string, string>
  >({});
  const [emptyMessage, setEmptyMessage] = useState<string>(
    ' Please click search button to fetch data '
  );
  const groupOpenAccount = getLocalGroupOpenAccount();
  const dispatch = useAppDispatch();

  const currentAccount = useAppSelector(selectCurrentAccount);
  const ou = useGetUserOrgNodesQuery({
    server: server,
    fcName: HomeElms?.fcName,
    refresh: true
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const handleExpanded = (rowData: any) => {
    let open = false;
    const nextExpandedRowKeys = [];

    expandedRowKeys.forEach((key) => {
      if (key === rowData[rowKey]) {
        open = true;
      } else {
        nextExpandedRowKeys.push(key);
      }
    });

    if (!open) {
      nextExpandedRowKeys.push(rowData[rowKey]);
    }

    setExpandedRowKeys(nextExpandedRowKeys);
  };
  useEffect(() => {
    if (ouStatus === 'succeeded' && currentGroup.id === MY_ACCOUNT_LIST) {
      const data = Object.values(accounts)?.map(
        (obj: Record<string, any>, idx: number) => {
          const ouPath = ouPathMap.get(generateAccountNodeId(obj)) || [];
          return {
            ...obj,
            index: idx + 1,
            parent_id: obj?.parent_id || -1,
            ouPath: ouPath,
            ou: obj?.account_company,
            regionName: obj?.region?.name || obj?.region?.alias,
            value: ouData?.length
              ? `${obj?.parent_id || -1}-${generateAccountNodeId(obj)}`
              : `${idx}-${obj?.account_id}`,
            children: [
              {
                ...obj,
                value: ouData?.length
                  ? `${obj?.parent_id}-${generateAccountNodeId(obj)}-children`
                  : `${idx}-${obj?.account_id}-children`,
                isAccount: true
              }
            ]
          };
        }
      );
      setTableData(data);
    }
  }, [ouStatus, currentGroup.id]);

  useEffect(() => {
    if (currentGroup.id !== MY_ACCOUNT_LIST) {
      setTableData([]);
      setEmptyMessage('Please click search button to fetch data');
    } else {
      setEmptyMessage('No data found');
    }
  }, [currentGroup.id]);

  useEffect(() => {
    if (sortData) {
      let newTreeData = sortData;
      if (currentGroup.id === MY_ACCOUNT_LIST && ouData) {
        const trees = ouData?.length
          ? getSelectedTrees(preSelectedNode, ouData)
          : ouData;
        const treeTable = trees.map((obj: any) =>
          generateNodeData(sortData, obj, 0, '1')
        );
        newTreeData = trees[0]?.value ? treeTable : sortData;
      }
      setTreeData(newTreeData);
      const openAccount =
        currentGroup.id === MY_ACCOUNT_LIST ? currentAccount : undefined;
      const expandedKeys = getAllExpandedKey(newTreeData, openAccount);
      setExpandedRowKeys(expandedKeys);
    }
  }, [sortData, preSelectedNode, currentGroup.id, ouData]);

  const [getInstance] = useLazyGetInstByAccountQuery();
  const [getAsset] = useLazyGetAssetByAccountQuery();
  const [getInstanceExtra] = useLazyGetInstanceExtraInfoQuery();
  useEffect(() => {
    if (!isRedirectValid && isRedirectChecked) {
      if (currentAccount?.region?.alias) {
        const region = regions[currentAccount.region.alias];
        if (region?.token) {
          getInstance({
            account: { ...currentAccount, region: { ...region } },
            queryHistoricData: true
          });
          getAsset({
            account: { ...currentAccount, region: { ...region } }
          });
          getInstanceExtra({
            account: { ...currentAccount, region: { ...region } }
          });
        }
      }
    }
  }, [currentAccount, isRedirectValid, isRedirectChecked, regions]);

  const getRowHeight = (rowData: any) => {
    return rowData?.isAccount ? 360 : 40;
  };
  const handleSortColumn = (sortColumn: any, sortType: any) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSortColumn(sortColumn);
      setSortType(sortType);
    }, 500);
  };
  const reloadData = async () => {
    await ou.refetch();
    dispatch(
      regionAssetApi.util.invalidateTags([
        { type: 'Asset', id: MY_ACCOUNT_LIST }
      ])
    );
    dispatch(
      regionInstanceApi.util.invalidateTags([
        { type: 'Instance', id: MY_ACCOUNT_LIST }
      ])
    );
    dispatch(
      regionUserHelperApi.util.invalidateTags([
        { type: 'Helper', id: MY_ACCOUNT_LIST }
      ])
    );
  };
  const [refreshHandler, setRefreshHandler] = useState<
    React.MouseEventHandler<HTMLElement> | undefined
  >(undefined);
  const renderEmpty = (info: any) => (
    <div
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center'
      }}
    >
      {emptyMessage}
    </div>
  );

  useEffect(() => {
    if (currentGroup.id === MY_ACCOUNT_LIST) {
      setRefreshHandler(() => reloadData);
    }
  }, [currentGroup.id]);

  useEffect(() => {
    if (ou.isLoading || ou.isFetching) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [ou.isLoading, ou.isFetching]);

  return (
    <div className="organization-container">
      <div className="organization-content">
        <div className="table-container">
          <OrgToolbar
            sortColumn={sortColumn}
            sortType={sortType}
            reloadData={refreshHandler}
            tableData={tableData}
            setSortData={setSortData}
            currentGroup={currentGroup}
            setGroupSearchKeyValue={setGroupSearchKeyValue}
          />
          <Table
            loading={loading}
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={handleSortColumn}
            isTree={true}
            cellBordered={false}
            rowKey="value"
            fillHeight
            headerHeight={40}
            rowHeight={getRowHeight}
            data={treeData?.length ? treeData : []}
            renderEmpty={renderEmpty}
            shouldUpdateScroll={false}
            onExpandChange={(isOpen, rowData) => {
              handleExpanded(rowData);
              if (isOpen) {
                if (currentGroup.id === MY_ACCOUNT_LIST) {
                  updateLocalLatestAccount(rowData);
                  dispatch(updateCurrentAccount(rowData));
                } else {
                  updateLocalGroupOpenAccount(rowData);
                  dispatch(updateCurrentGroupAccount(rowData));
                }
                console.info(isOpen, '----------- rowData', rowData);
              }
            }}
            renderTreeToggle={(icon, rowData) => {
              if (rowData && rowData[rowKey] && rowData?.account_id) {
                const myIcon = expandedRowKeys.includes(rowData?.[rowKey]) ? (
                  <CollaspedOutlineIcon />
                ) : (
                  <ExpandOutlineIcon />
                );
                return myIcon;
              }
              return icon;
            }}
            renderRow={(children: any, rowData: any) => {
              if (rowData?.isAccount) {
                return <ExpandedRow rowData={rowData} />;
              }
              return children;
            }}
            expandedRowKeys={expandedRowKeys}
          >
            {renderNormalRow(
              Math.max(
                ouData?.map((o: Record<string, any>) => o?.maxLevel || 0)
              ) || 0,
              currentGroup.id !== MY_ACCOUNT_LIST
            )}
          </Table>
          {currentGroup.id !== MY_ACCOUNT_LIST && (
            <OrgPagination
              currentGroup={currentGroup}
              setRefreshHandler={setRefreshHandler}
              setTableData={setTableData}
              setLoading={setLoading}
              setEmptyMessage={setEmptyMessage}
              sortColumn={sortColumn}
              setSortColumn={setSortColumn}
              sortType={sortType}
              groupSearchKeyValue={groupSearchKeyValue}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default Organization;
const getAllExpandedKey = (
  treeData: Record<string, any>[],
  latestAccount?: Record<string, any>
): string[] => {
  const arr = treeData?.map((obj: Record<string, any>) => {
    return (obj?.hasAccount && !obj?.account_id) ||
      (obj?.account_id &&
        (latestAccount?.account_id
          ? latestAccount.account_id === obj.account_id
          : obj.user_type === 'Master' || obj.user_type === 'IAM'))
      ? [obj?.value, getAllExpandedKey(obj?.children, latestAccount)]
      : '';
  });
  const res = arr?.flat(4).filter((val) => val);
  return res;
};

const generateNodeData = (
  accounts: Record<string, any>[],
  node: ITreeData,
  level: number,
  value: string
) => {
  const getAccountsNumber = (
    accounts: Record<string, any>,
    node: ITreeData
  ) => {
    return accounts.filter((obj: Record<string, any>) =>
      obj?.ouPath?.includes(node?.label)
    ).length;
  };
  const getOuName = (num: string, node: ITreeData) => {
    return num && node?.data?.node_type === 'org'
      ? `${node?.label} (${num})`
      : `${node?.label}`;
  };
  const num = getAccountsNumber(accounts, node);
  const name = getOuName(num, node);
  const subAccounts: Record<string, any>[] = node?.children
    ? node?.children.map((child, idx: number) =>
        generateNodeData(accounts, child, level + 1, `${value}-${idx + 1}`)
      )
    : [];
  const children = [...subAccounts];
  const nodeAccount =
    node?.data?.node_type === 'account'
      ? // match the node's full identity (org_id / access_from_user_id included)
        // because generateAccountNodeId is NOT unique for direct vs org-member
        accounts.find(
          (obj: Record<string, any>) =>
            String(obj?.account_node_id) ===
              String(node?.data?.account_node_id) &&
            String(obj?.account_id) === String(node?.data?.account_id) &&
            String(obj?.user_id) === String(node?.data?.user_id) &&
            String(obj?.org_id ?? '') === String(node?.data?.org_id ?? '') &&
            String(obj?.access_from_user_id ?? '') ===
              String(node?.data?.access_from_user_id ?? '')
        ) ||
        accounts.find(
          (obj: Record<string, any>) =>
            generateAccountNodeId(obj) === node?.nodeId && obj?.region?.url
        ) ||
        accounts.find(
          (obj: Record<string, any>) =>
            generateAccountNodeId(obj) === node?.nodeId
        ) ||
        {}
      : {};

  return {
    ...nodeAccount,
    ou: name,
    hasAccount: num > 0,
    level: level,
    rootNode: node?.rootNode,
    value: value,
    childrenNum: children.length,
    children: children.length
      ? children
      : nodeAccount?.children?.map((obj: Record<string, any>) => ({
          ...obj,
          level: level + 1
        }))
  };
};
